import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  Input,
  signal,
  inject,
  input,
  ChangeDetectionStrategy,
} from '@angular/core';

import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { billingPlanTokenRecord } from '@core/billing-util';
import { filterBoolean } from '@core/common-legacy';
import type { IconName } from '@core/user-interface-legacy';
import {
  DropdownItemComponent,
  HeadingComponent,
  IconComponent,
} from '@core/user-interface-legacy';

import { FeatureGuardFacade } from '../feature-guard.facade';
import type {
  FeatureGuardComponent,
  FeatureGuardMode,
  GuardedFeature,
} from '../feature-guard.types';

@Component({
  selector: 'rt-feature-guard-dropdown-item',
  templateUrl: `feature-guard-dropdown-item.component.html`,
  styleUrls: ['feature-guard-dropdown-item.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [NgTemplateOutlet, DropdownItemComponent, IconComponent, HeadingComponent],
})
export class FeatureGuardDropdownItemComponent implements FeatureGuardComponent {
  protected readonly cdRef = inject(ChangeDetectorRef);
  public readonly facade = inject(FeatureGuardFacade);

  public readonly iconName = input<IconName>(undefined!);
  public readonly heading = input<string>(undefined!);
  public readonly subheading = input<string>(undefined!);
  public readonly useOriginalTemplate = input<boolean>(undefined!);
  @Input() public mode: FeatureGuardMode;
  @Input() public feature: GuardedFeature;

  public enabled: boolean = false;
  public features: string[];
  public isProtected: boolean = true;

  public colorToken = signal<string | null>(null);

  private ngOnDestroy$ = new Subject<void>();

  public ngOnInit() {
    if (!this.feature) {
      this.isProtected = false;
    } else {
      this.facade
        .checkFeatureAvailability(this.feature)
        .pipe(takeUntil(this.ngOnDestroy$.asObservable()))
        .subscribe(enabled => {
          this.enabled = enabled;
          this.cdRef.detectChanges();
        });
    }

    this.facade
      .getPlanWhereFeatureIsAvailable(this.feature)
      .pipe(filterBoolean, takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe(availableFromPlan => {
        this.colorToken.set(billingPlanTokenRecord[availableFromPlan]);
      });
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }
}
